import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Flame, Dumbbell, TrendingDown, TrendingUp, Calendar,
  ChevronRight, Trophy, Sparkles, CheckCircle2, Loader2, ArrowRight,
} from "lucide-react";
import { useUserAuth, getUserToken } from "../context/UserAuthContext";
import UserNav from "../components/UserNav";

const API = process.env.REACT_APP_BACKEND_URL;

export default function InicioPage() {
  const { user } = useUserAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getUserToken();
    if (!token) { navigate("/login", { replace: true }); return; }
    fetch(`${API}/api/inicio/resumen`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <Loader2 size={24} className="text-[#F2C230] animate-spin" />
    </div>
  );

  const { racha, entreno_hoy, progreso_meta, estadisticas_mes, record_reciente, contenido_nuevo } = data || {};
  const nombre = user?.nombre || user?.email?.split("@")[0] || "socio";
  const hora = new Date().getHours();
  const saludo = hora < 14 ? "Buenos días" : hora < 21 ? "Buenas tardes" : "Buenas noches";

  return (
    <div className="min-h-screen bg-black text-white" data-testid="inicio-page">
      <UserNav />
      <div className="max-w-lg mx-auto px-4 py-6 space-y-4 pb-24">

        {/* Greeting */}
        <div className="pt-2">
          <p className="text-xs text-white/60 uppercase tracking-widest font-medium">{saludo}</p>
          <h1 className="text-2xl font-black mt-0.5 capitalize">{nombre}</h1>
        </div>

        {/* ── 1. RACHA ───────────────────────────────────────── */}
        <RachaCard racha={racha} />

        {/* ── 2. ENTRENO DE HOY ──────────────────────────────── */}
        <EntrenoHoyCard entreno={entreno_hoy} />

        {/* ── 3. PROGRESO HACIA LA META ──────────────────────── */}
        {progreso_meta?.tiene_datos !== false && progreso_meta && (
          <ProgresoCard progreso={progreso_meta} />
        )}

        {/* ── 4. ESTADÍSTICAS DEL MES ────────────────────────── */}
        <StatsCard stats={estadisticas_mes} record={record_reciente} />

        {/* ── 5. CONTENIDO NUEVO ─────────────────────────────── */}
        {contenido_nuevo?.length > 0 && (
          <NuevoContenidoCard items={contenido_nuevo} />
        )}
      </div>
    </div>
  );
}

// ── Racha ──────────────────────────────────────────────────────
function RachaCard({ racha }) {
  if (!racha) return null;
  const { semanas, activa, sesiones_esta_semana, dias_objetivo_semana } = racha;

  return (
    <div className="relative overflow-hidden border border-white/10 bg-[#0d0d0d] p-5" data-testid="racha-card">
      {activa && semanas > 0 && (
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/8 to-transparent pointer-events-none" />
      )}
      <div className="flex items-center justify-between">
        <div>
          {activa && semanas > 0 ? (
            <>
              <div className="flex items-center gap-2">
                <Flame size={22} className="text-orange-400" />
                <span className="text-3xl font-black tabular-nums">{semanas}</span>
                <span className="text-lg font-bold text-white/70">{semanas === 1 ? "semana" : "semanas"}</span>
              </div>
              <p className="text-sm text-white/70 mt-1">consecutivas cumpliendo tu plan</p>
            </>
          ) : (
            <>
              <p className="text-lg font-black text-white">Esta semana toca reengancharse</p>
              <p className="text-sm text-white/70 mt-1">Cada entreno cuenta, sin importar el pasado</p>
            </>
          )}
        </div>
        <div className="text-right">
          <p className="text-2xl font-black tabular-nums text-[#F2C230]">{sesiones_esta_semana}<span className="text-base font-bold text-white/60">/{dias_objetivo_semana}</span></p>
          <p className="text-[11px] uppercase tracking-widest text-white/60 mt-0.5 font-bold">esta semana</p>
        </div>
      </div>
      {/* Progress bar */}
      <div className="mt-4 h-1 bg-white/8 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#F2C230] transition-all duration-500"
          style={{ width: `${Math.min(100, (sesiones_esta_semana / dias_objetivo_semana) * 100)}%` }}
        />
      </div>
    </div>
  );
}

// ── Entreno de hoy ─────────────────────────────────────────────
function EntrenoHoyCard({ entreno }) {
  if (!entreno) return null;
  const { estado, dia_rutina, total_dias, tiene_rutina, ejercicios_preview } = entreno;

  if (!tiene_rutina) return (
    <div className="border border-white/10 bg-[#0d0d0d] p-5" data-testid="entreno-hoy-card">
      <p className="text-xs uppercase tracking-widest text-white/65 mb-1">Tu entreno de hoy</p>
      <p className="text-sm text-white/75">Aún no tienes rutina asignada.</p>
      <Link to="/mi-rutina" className="inline-flex items-center gap-1.5 mt-3 text-[#F2C230] text-xs font-bold hover:text-white transition-colors">
        Ver catálogo de rutinas <ChevronRight size={13} />
      </Link>
    </div>
  );

  if (estado === "completado") return (
    <div className="border border-[#F2C230]/30 bg-[#F2C230]/5 p-5" data-testid="entreno-hoy-card">
      <div className="flex items-center gap-3">
        <CheckCircle2 size={24} className="text-[#F2C230] shrink-0" />
        <div>
          <p className="text-xs uppercase tracking-widest text-[#F2C230]/70 mb-0.5">Entreno de hoy</p>
          <p className="font-black text-white">¡Completado! Día {dia_rutina} de {total_dias}</p>
          <p className="text-xs text-white/50 mt-0.5">Buen trabajo, ya puedes descansar</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="border border-white/12 bg-[#0d0d0d] p-5" data-testid="entreno-hoy-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/70 mb-0.5">Tu entreno de hoy</p>
          <p className="font-black text-lg">Día {dia_rutina} <span className="text-white/65 text-sm font-medium">de {total_dias}</span></p>
        </div>
        <Dumbbell size={20} className="text-white/40" />
      </div>

      {ejercicios_preview.length > 0 && (
        <div className="space-y-2 mb-4">
          {ejercicios_preview.map((ej, i) => (
            <div key={i} className="flex items-center justify-between text-sm">
              <span className="text-white font-medium">{ej.nombre}</span>
              <span className="text-white/70 text-xs tabular-nums shrink-0">{ej.series}×{ej.repeticiones}</span>
            </div>
          ))}
          {ejercicios_preview.length === 4 && (
            <p className="text-xs text-white/60">+ más ejercicios</p>
          )}
        </div>
      )}

      <Link
        to="/mi-rutina"
        data-testid="empezar-entreno-btn"
        className="flex items-center justify-center gap-2 w-full bg-[#F2C230] text-black font-black text-sm tracking-widest uppercase py-3 hover:bg-white transition-colors"
      >
        Empezar entreno <ArrowRight size={15} />
      </Link>
    </div>
  );
}

// ── Progreso meta ──────────────────────────────────────────────
function ProgresoCard({ progreso }) {
  const { peso_actual, peso_inicial, objetivo_peso_kg, diferencia_total, diferencia_meta } = progreso;
  const bajando = diferencia_total <= 0;
  const Icon = bajando ? TrendingDown : TrendingUp;
  const hasMeta = objetivo_peso_kg != null;

  return (
    <Link to="/mi-evolucion" className="block border border-white/15 bg-[#0d0d0d] p-5 hover:border-white/35 transition-colors group" data-testid="progreso-card">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-white/70 mb-1 font-bold">Progreso</p>
          {hasMeta && diferencia_meta !== null ? (
            diferencia_meta <= 0 ? (
              <p className="text-lg font-black text-[#F2C230]">¡Meta alcanzada!</p>
            ) : (
              <p className="text-lg font-black">Te quedan <span className="text-[#F2C230]">{Math.abs(diferencia_meta)} kg</span> para tu meta</p>
            )
          ) : (
            <p className="text-lg font-black">
              <span className={bajando ? "text-green-400" : "text-orange-400"}>
                {diferencia_total > 0 ? "+" : ""}{diferencia_total} kg
              </span> desde el inicio
            </p>
          )}
          <p className="text-xs text-white/65 mt-1">{peso_actual} kg actuales{hasMeta ? ` · meta ${objetivo_peso_kg} kg` : ""}</p>
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          <Icon size={18} className={bajando ? "text-green-400" : "text-orange-400"} />
          <ChevronRight size={14} className="text-white/45 group-hover:text-white/80 transition-colors" />
        </div>
      </div>
    </Link>
  );
}

// ── Estadísticas del mes ───────────────────────────────────────
function StatsCard({ stats, record }) {
  if (!stats) return null;
  const { sesiones_completadas, sesiones_planificadas } = stats;
  const pct = Math.min(100, Math.round((sesiones_completadas / sesiones_planificadas) * 100)) || 0;

  return (
    <div className="border border-white/10 bg-[#0d0d0d] p-5" data-testid="stats-card">
      <p className="text-[10px] uppercase tracking-widest text-white/70 mb-4 font-bold">Este mes</p>
      <div className="flex items-end gap-3 mb-3">
        <span className="text-4xl font-black tabular-nums">{sesiones_completadas}</span>
        <span className="text-base text-white/70 mb-1 font-semibold">de {sesiones_planificadas} entrenos</span>
      </div>
      <div className="h-1.5 bg-white/8 rounded-full overflow-hidden">
        <div className="h-full bg-[#F2C230] rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-white/65 mt-2 font-medium">{pct}% del objetivo mensual</p>

      {record && (
        <div className="mt-4 pt-4 border-t border-white/8 flex items-start gap-2">
          <Trophy size={14} className="text-[#F2C230] shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-bold text-white/80">Nuevo máximo en {record.ejercicio}</p>
            <p className="text-xs text-white/75">{record.peso_max} kg <span className="text-white/55">· antes {record.mes_anterior} kg</span></p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Contenido nuevo ────────────────────────────────────────────
function NuevoContenidoCard({ items }) {
  return (
    <div className="border border-white/8 bg-white/[0.02] p-4" data-testid="nuevo-contenido-card">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={13} className="text-[#F2C230]" />
        <p className="text-[10px] uppercase tracking-widest text-white/75 font-bold">Nuevo esta semana</p>
      </div>
      <div className="space-y-2">
        {items.map((item) => (
          <Link
            key={item.id}
            to={item.numero_maquina ? `/maquina/${item.numero_maquina}` : "/catalogo"}
            className="flex items-center justify-between text-sm text-white/85 hover:text-[#F2C230] transition-colors"
            data-testid={`nuevo-item-${item.id}`}
          >
            <span>{item.nombre}</span>
            <ChevronRight size={13} className="shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}
